// Agent Run Inspector — given a task UUID, renders the per-task agent runs
// from GET /tasks/{task_uuid}/agent-runs.
//
// Empty state is a text input the operator can paste into. Also subscribes
// to the cross-widget `agent-board:inspect-task` event (see
// src/lib/agentBoardEvents.ts), so clicking "inspect" on a row in Widget 1
// (Owner Approval Queue) populates the input, fetches the runs, and scrolls
// this widget into view.
//
// "View audit chain" loads from GET /api/v1/agent-audit-events
// (filterable by agent_run_id), shipped in seitiate#35 (2026-05-04). For
// each loaded run, click the "audit" affordance to expand its event chain.

import { useEffect, useState } from 'react';
import useSWR from 'swr';
import { listAgentAuditEvents } from '@/api/agentAuditEvents';
import { listAgentRunsForTask } from '@/api/agentTasks';
import type {
  AgentAuditEventRead,
  AgentRunRead,
  AgentRunStatus,
  PolicyDecisionKind,
} from '@/api/agentBoardTypes';
import { EngineHttpError } from '@/lib/engineHttp';
import { onInspectTask } from '@/lib/agentBoardEvents';
import type { WidgetDefinition, WidgetProps } from '@/types/widget';

const STATUS_COLOR: Record<AgentRunStatus, string> = {
  pending: 'var(--sp-text-dim)',
  started: 'var(--sp-accent)',
  running: 'var(--sp-accent)',
  approval_required: 'var(--sp-warn)',
  completed: 'var(--sp-ok)',
  halted: 'var(--sp-error)',
  failed: 'var(--sp-error)',
  blocked: 'var(--sp-error)',
};

function durationLabel(startedAt: string, endedAt: string | null | undefined): string {
  const start = new Date(startedAt).getTime();
  const end = endedAt ? new Date(endedAt).getTime() : Date.now();
  const diff = Math.max(0, end - start);
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  if (hours < 24) return rem ? `${hours}h ${rem}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

function fetcher(taskUuid: string): Promise<AgentRunRead[]> {
  return listAgentRunsForTask(taskUuid);
}

function AgentRunInspectorWidget({ context: _context }: WidgetProps) {
  const [input, setInput] = useState('');
  const [taskUuid, setTaskUuid] = useState<string | null>(null);

  const swrKey = taskUuid ? `agent-board:runs:${taskUuid}` : null;
  const { data, error, isLoading } = useSWR<AgentRunRead[]>(
    swrKey,
    () => fetcher(taskUuid as string),
    { revalidateOnFocus: false },
  );

  // Listen for cross-widget inspect requests (e.g. from Widget 1's
  // approval row "inspect" button). On receipt, populate the input,
  // load the runs, and scroll this widget into view.
  useEffect(() => {
    let frame = 0;
    const unsubscribe = onInspectTask(({ taskUuid: incoming }) => {
      setInput(incoming);
      setTaskUuid(incoming);
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const el = document.getElementById('widget-agent-run-inspector');
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
    return () => {
      unsubscribe();
      cancelAnimationFrame(frame);
    };
  }, []);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = input.trim();
    if (trimmed) {
      setTaskUuid(trimmed);
    }
  }

  return (
    <div id="widget-agent-run-inspector" className="sp-widget">
      <div className="sp-widget-header">
        <span>Agent Run Inspector</span>
        {taskUuid ? (
          <button
            type="button"
            onClick={() => {
              setTaskUuid(null);
              setInput('');
            }}
            style={{
              fontSize: 11,
              fontWeight: 400,
              letterSpacing: 0,
              border: 'none',
              background: 'transparent',
              color: 'var(--sp-text-dim)',
              cursor: 'pointer',
            }}
          >
            Clear
          </button>
        ) : null}
      </div>

      <div className="sp-widget-body" style={{ padding: 12, fontSize: 12 }}>
        <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Enter a task UUID to inspect its agent runs."
            aria-label="Task UUID"
            style={{
              flex: 1,
              minWidth: 0,
              background: 'transparent',
              border: '1px solid var(--sp-border)',
              borderRadius: 3,
              padding: '4px 6px',
              color: 'var(--sp-text)',
              fontFamily: 'inherit',
              fontSize: 12,
            }}
          />
          <button
            type="submit"
            disabled={!input.trim()}
            style={{
              fontSize: 11,
              padding: '4px 10px',
              border: '1px solid var(--sp-border)',
              background: 'transparent',
              color: 'var(--sp-accent)',
              borderRadius: 3,
              cursor: input.trim() ? 'pointer' : 'not-allowed',
            }}
          >
            Inspect
          </button>
        </form>

        {!taskUuid ? (
          <div style={{ color: 'var(--sp-text-dim)', fontSize: 11 }}>
            Paste a task UUID above to load its agent run history.
          </div>
        ) : isLoading ? (
          <div style={{ color: 'var(--sp-text-muted)' }}>Loading…</div>
        ) : error ? (
          <div>
            <div style={{ color: 'var(--sp-error)' }}>
              {error instanceof EngineHttpError
                ? `${error.status}: ${error.message}`
                : error instanceof Error
                  ? error.message
                  : 'Failed to load.'}
            </div>
            <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 11, marginTop: 4 }}>
              GET /api/v1/tasks/{taskUuid}/agent-runs
            </div>
          </div>
        ) : !data || data.length === 0 ? (
          <div style={{ color: 'var(--sp-text-muted)' }}>
            No agent runs recorded for this task.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {data.map((run) => (
              <RunCard key={run.id} run={run} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RunCard({ run }: { run: AgentRunRead }) {
  const color = STATUS_COLOR[run.status] ?? 'var(--sp-text-dim)';
  return (
    <div
      style={{
        border: '1px solid var(--sp-border)',
        borderRadius: 3,
        padding: 8,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          marginBottom: 4,
        }}
      >
        <span
          style={{
            fontSize: 9,
            padding: '1px 5px',
            borderRadius: 2,
            background: color,
            color: 'var(--sp-bg, #000)',
            textTransform: 'uppercase',
            letterSpacing: 0.4,
          }}
        >
          {run.status}
        </span>
        <span className="mono" style={{ fontSize: 11, color: 'var(--sp-text-dim)' }}>
          run/{run.id.slice(0, 8)}
        </span>
        <span style={{ fontSize: 11, color: 'var(--sp-text-muted)' }}>
          {durationLabel(run.started_at, run.ended_at)}
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 8px', fontSize: 11 }}>
        {run.model_provider || run.model_name ? (
          <>
            <span style={{ color: 'var(--sp-text-dim)' }}>model</span>
            <span className="mono">
              {run.model_provider ?? '?'}/{run.model_name ?? '?'}
            </span>
          </>
        ) : null}
        {run.branch ? (
          <>
            <span style={{ color: 'var(--sp-text-dim)' }}>branch</span>
            <span className="mono">{run.branch}</span>
          </>
        ) : null}
        {run.pr_url ? (
          <>
            <span style={{ color: 'var(--sp-text-dim)' }}>pr</span>
            <a href={run.pr_url} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all' }}>
              {run.pr_url}
            </a>
          </>
        ) : null}
        {run.tests_status ? (
          <>
            <span style={{ color: 'var(--sp-text-dim)' }}>tests</span>
            <span className="mono">{run.tests_status}</span>
          </>
        ) : null}
        {run.evidence_uri ? (
          <>
            <span style={{ color: 'var(--sp-text-dim)' }}>evidence</span>
            <a
              href={run.evidence_uri}
              target="_blank"
              rel="noreferrer"
              style={{ wordBreak: 'break-all' }}
            >
              {run.evidence_uri}
            </a>
          </>
        ) : null}
        {run.halt_reason ? (
          <>
            <span style={{ color: 'var(--sp-text-dim)' }}>halt</span>
            <span style={{ color: 'var(--sp-error)' }}>{run.halt_reason}</span>
          </>
        ) : null}
      </div>
      <RunAuditChain runId={run.id} />
    </div>
  );
}

const DECISION_COLOR: Record<PolicyDecisionKind, string> = {
  allowed: 'var(--sp-ok)',
  approval_required: 'var(--sp-warn)',
  denied: 'var(--sp-error)',
  halted: 'var(--sp-error)',
};

function RunAuditChain({ runId }: { runId: string }) {
  const [open, setOpen] = useState(false);
  const swrKey = open ? `agent-board:audit:run:${runId}` : null;
  const { data, error, isLoading } = useSWR(
    swrKey,
    () => listAgentAuditEvents({ agent_run_id: runId, limit: 100 }),
    { revalidateOnFocus: false },
  );

  return (
    <div style={{ marginTop: 8, borderTop: '1px solid var(--sp-border)', paddingTop: 6 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          fontSize: 10,
          padding: '2px 8px',
          border: '1px solid var(--sp-border)',
          background: 'transparent',
          color: 'var(--sp-text-muted)',
          borderRadius: 3,
          cursor: 'pointer',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
        }}
      >
        {open ? '▾ audit chain' : '▸ audit chain'}
      </button>
      {open ? (
        <div style={{ marginTop: 6 }}>
          {error ? (
            <div style={{ color: 'var(--sp-error)', fontSize: 11 }}>
              {error instanceof EngineHttpError
                ? `${error.status}: ${error.message}`
                : error instanceof Error
                  ? error.message
                  : 'Failed to load audit chain.'}
            </div>
          ) : isLoading || !data ? (
            <div style={{ color: 'var(--sp-text-muted)', fontSize: 11 }}>Loading…</div>
          ) : data.items.length === 0 ? (
            <div style={{ color: 'var(--sp-text-dim)', fontSize: 11 }}>
              No audit events recorded for this run.
            </div>
          ) : (
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
              }}
            >
              {data.items.map((evt) => (
                <AuditEventRow key={evt.id} event={evt} />
              ))}
              {data.count > data.items.length ? (
                <li style={{ fontSize: 10, color: 'var(--sp-text-dim)', padding: '2px 0' }}>
                  Showing {data.items.length} of {data.count}.
                </li>
              ) : null}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

function AuditEventRow({ event }: { event: AgentAuditEventRead }) {
  const decisionColor = DECISION_COLOR[event.policy_decision] ?? 'var(--sp-text-dim)';
  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto auto 1fr',
        gap: 6,
        alignItems: 'baseline',
        fontSize: 11,
        padding: '2px 0',
        borderLeft: `2px solid ${decisionColor}`,
        paddingLeft: 6,
      }}
    >
      <span className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)' }}>
        {new Date(event.created_at).toLocaleTimeString()}
      </span>
      <span
        style={{
          fontSize: 9,
          padding: '0 4px',
          borderRadius: 2,
          background: decisionColor,
          color: 'var(--sp-bg, #000)',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
        }}
      >
        {event.policy_decision}
      </span>
      <span className="mono" style={{ color: 'var(--sp-text)' }}>
        {event.action_type}
      </span>
    </li>
  );
}

export const agentRunInspectorWidget: WidgetDefinition = {
  id: 'agent-run-inspector',
  name: 'Agent Run Inspector',
  description: 'Per-task agent run history with status, branch, PR, tests, and evidence.',
  icon: 'search',
  category: 'agents',
  scope: 'both',
  defaultSize: { w: 6, h: 6 },
  minSize: { w: 4, h: 4 },
  component: AgentRunInspectorWidget,
};
